import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import { toast } from '../../../components/LsToast'
import { ReprintDialog } from '../../../components/ReprintDialog'
import { TerminalPaymentWaitingDialog } from '../../../components/TerminalPaymentWaitingDialog'
import { usePosId } from '../../../hooks/usePosId'
import { usePrinter } from '../../../hooks/usePrinter'
import { usePrintingEnabled } from '../../../hooks/usePrintingEnabled'
import { printReceipt } from '../../../service/print.service'
import { createReceipt, getReceipt } from '../../../service/receipts.service'
import { createTerminalPayment } from '../../../service/terminal-payments.service'
import { BookingDto, PaymentType, ReceiptDto } from '../../../service/types'
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage'

type Props = {
  open: boolean
  onClose: () => void
  onPaid: () => void
  bookings: BookingDto[]
}

type CustomerGroup = {
  customerId: string
  customerName: string
  bookings: BookingDto[]
}

export const CheckoutDialog: FC<Props> = ({ open, onClose, onPaid, bookings }) => {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const { posId } = usePosId()
  const { printerIp } = usePrinter()
  const { printingEnabled } = usePrintingEnabled()
  const [reprintOpen, setReprintOpen] = useState(false)
  const [reprintReceipt, setReprintReceipt] = useState<ReceiptDto | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [terminalPaymentId, setTerminalPaymentId] = useState<string | null>(null)

  const unpaidBookings = useMemo(() => bookings.filter((b) => !b.receiptId), [bookings])

  const customerGroups = useMemo(() => {
    const map = new Map<string, CustomerGroup>()
    for (const b of unpaidBookings) {
      const existing = map.get(b.customerId)
      if (existing) {
        existing.bookings.push(b)
      } else {
        map.set(b.customerId, {
          customerId: b.customerId,
          customerName: b.customer?.name ?? b.customerId,
          bookings: [b],
        })
      }
    }
    return [...map.values()]
  }, [unpaidBookings])

  const [selectedCustomerId, setSelectedCustomerId] = useState('')

  // Auto-select only when single customer; require explicit selection for multiple
  const activeCustomerId = customerGroups.length === 1 ? customerGroups[0]?.customerId || '' : selectedCustomerId

  const activeGroup = customerGroups.find((g) => g.customerId === activeCustomerId)
  const activeBookings = activeGroup?.bookings ?? []

  const totalBase = activeBookings.reduce((sum, b) => sum + Number(b.basePrice), 0)
  const totalVat = activeBookings.reduce((sum, b) => sum + Number(b.vat), 0)
  const totalPrice = activeBookings.reduce((sum, b) => sum + Number(b.price), 0)

  const handlePrintAndClose = async (receiptId: string) => {
    toast.success(t('general.notification.savedSuccessfully'))
    onPaid()
    onClose()
    if (printingEnabled && printerIp) {
      let receiptData: ReceiptDto | null = null
      try {
        const res = await getReceipt(receiptId)
        receiptData = res.data
        await printReceipt(printerIp, receiptData, t)
        toast.success(t('print.success'))
      } catch {
        if (receiptData) {
          setReprintReceipt(receiptData)
          setReprintOpen(true)
        }
      }
    }
  }

  const handlePayCash = async () => {
    if (!activeCustomerId || activeBookings.length === 0) return
    setSubmitting(true)
    try {
      const receipt = await createReceipt({
        customerId: activeCustomerId,
        bookingIds: activeBookings.map((b) => b.id),
        paymentType: PaymentType.Cash,
        posId,
      })
      await handlePrintAndClose(receipt.data.id)
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    } finally {
      setSubmitting(false)
    }
  }

  const handlePayCard = async () => {
    if (!activeCustomerId || activeBookings.length === 0 || !posId) return
    setSubmitting(true)
    try {
      const result = await createTerminalPayment({
        price: Math.round(totalPrice * 100),
        curr: 'CZK',
        bookingIds: activeBookings.map((b) => b.id),
        customerId: activeCustomerId,
        posId,
      })
      setTerminalPaymentId(result.data.paymentId)
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
      setSubmitting(false)
    }
  }

  const handleTerminalPaid = async (receiptId: string) => {
    setTerminalPaymentId(null)
    setSubmitting(false)
    await handlePrintAndClose(receiptId)
  }

  const handleTerminalCancelled = () => {
    setTerminalPaymentId(null)
    setSubmitting(false)
    toast.error(t('terminalPayment.cancelled'))
  }

  const handleRetryPrint = async () => {
    if (!reprintReceipt || !printerIp) return
    setRetrying(true)
    try {
      const fullReceipt = await getReceipt(reprintReceipt.id)
      await printReceipt(printerIp, fullReceipt.data, t)
      toast.success(t('print.success'))
      setReprintOpen(false)
      setReprintReceipt(null)
    } catch {
      toast.error(t('print.failed'))
    } finally {
      setRetrying(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('checkout.title')}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {customerGroups.length > 1 && (
            <TextField
              select
              label={t('lessons.form.customer')}
              value={activeCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            >
              {customerGroups.map((g) => (
                <MenuItem key={g.customerId} value={g.customerId}>
                  {g.customerName}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('receipts.detail.skierName')}</TableCell>
                  <TableCell align="right">{t('receipts.detail.price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeBookings.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      {b.skierName} ({b.skierAge})
                    </TableCell>
                    <TableCell align="right">{Number(b.price).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{t('checkout.netAmount')}</Typography>
              <Typography variant="body2">{totalBase.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{t('checkout.vat')}</Typography>
              <Typography variant="body2">{totalVat.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {t('checkout.totalAmount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {totalPrice.toFixed(2)}
              </Typography>
            </Box>
          </Paper>
          {(!posId || (printingEnabled && !printerIp)) && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {!posId ? t('checkout.posIdRequired') : t('print.noPrinter')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={handlePayCash}
            disabled={submitting || activeBookings.length === 0 || !posId || (printingEnabled && !printerIp)}
            fullWidth
          >
            {t('checkout.payWithCash')}
          </Button>
          <Button
            variant="contained"
            onClick={handlePayCard}
            disabled={submitting || activeBookings.length === 0 || !posId}
            fullWidth
          >
            {t('checkout.payWithCard')}
          </Button>
        </DialogActions>
      </Dialog>
      <ReprintDialog
        open={reprintOpen}
        onCancel={() => setReprintOpen(false)}
        onRetry={handleRetryPrint}
        retrying={retrying}
      />
      {terminalPaymentId && (
        <TerminalPaymentWaitingDialog
          paymentId={terminalPaymentId}
          open={true}
          onPaid={handleTerminalPaid}
          onCancelled={handleTerminalCancelled}
        />
      )}
    </>
  )
}
